import { ChevronUp, ChevronDown } from "lucide-react";
import { ModuleIntro } from "@/components/ModuleIntro";
import { EXO_DRIVERS, EXO_CASES } from "@/data/driversExo";
import { HomeLink, BulletBlock, DRIVER_IMAGE, DRIVER_BY_KEY } from "@/components/exo/driversExoShared";

export const Welcome = ({ onStartFresh, hasSaved, onResume, inviteSlot }) => (
  <div className="animate-fade-up text-center">
    <HomeLink />
    <img src="/modules/drivers-exo.jpg" alt="" className="w-full h-52 sm:h-64 object-cover rounded-3xl mb-8" data-testid="exo-welcome-hero" />
    <div className="mb-6">
      <p className="text-xs uppercase tracking-[0.3em] text-[var(--gold-light)] mb-4">Identifier · Calmer · Transformer</p>
      <h1 className="font-serif-title text-4xl sm:text-5xl lg:text-6xl text-[var(--gold)] leading-tight">Travail autour des drivers relationnels</h1>
    </div>
    <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto mb-8">
      Cet exercice fait suite à l'Inventaire des Drivers Relationnels. Pour chacun des cinq drivers,
      vous découvrirez son fonctionnement puis vous raconterez vos propres situations. En fin
      d'exercice, vous évaluerez chaque driver et établirez votre propre classement.
    </p>
    <ModuleIntro moduleKey="drivers-exo" lang="fr" className="mb-8" />
    {inviteSlot}
    {hasSaved ? (
      <div className="flex flex-col items-center gap-4">
        <p className="text-sm text-[var(--ink-soft)] bg-[var(--sand)] border border-[var(--border)] rounded-full px-4 py-2" data-testid="exo-resume-notice">
          Une rédaction en cours a été retrouvée sur cet appareil.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button onClick={onResume} data-testid="exo-btn-resume" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">Reprendre ma rédaction</button>
          <button onClick={onStartFresh} data-testid="exo-btn-fresh" className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">Recommencer à zéro</button>
        </div>
      </div>
    ) : (
      <button onClick={onStartFresh} data-testid="exo-btn-start" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">Commencer l'exercice</button>
    )}
  </div>
);

export const DriverPage = ({ driver, index, total, entry, onChange, onPrev, onNext }) => {
  const setCase = (caseKey, val) => onChange({ ...entry, [caseKey]: { text: val } });

  return (
    <div className="animate-fade-up">
      <HomeLink />
      <div className="mb-6">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-sm uppercase tracking-[0.18em] text-[var(--ink-soft)]">Driver {index + 1} sur {total}</span>
          <span className="font-serif-title text-2xl text-[var(--gold)]">{Math.round(((index + 1) / total) * 100)} %</span>
        </div>
        <div className="h-3 w-full rounded-full bg-[var(--beige)] overflow-hidden">
          <div className="h-full rounded-full bg-[var(--gold)] transition-all duration-500" style={{ width: `${((index + 1) / total) * 100}%` }} />
        </div>
      </div>

      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-6" data-testid={`exo-driver-${driver.key}`}>
        <img src={DRIVER_IMAGE(driver.key)} alt="" loading="lazy" className="w-full h-40 sm:h-56 object-cover rounded-2xl mb-5" data-testid={`exo-driver-img-${driver.key}`} />
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-serif-title text-3xl sm:text-4xl" style={{ color: driver.color }}>Driver : {driver.name}</h2>
          <span className="rounded-full px-4 py-1.5 text-sm text-white" style={{ backgroundColor: driver.color }} data-testid={`exo-antidote-${driver.key}`}>
            Antidote — {driver.antidote}
          </span>
        </div>
        <p className="font-serif-title text-xl text-[var(--ink)] italic mb-5">{driver.slogan}</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <BulletBlock title="Injonction interne" items={driver.injonction} color={driver.color} />
          <BulletBlock title="Messages internes" items={driver.messages} color={driver.color} />
          <BulletBlock title="Points positifs" items={driver.positifs} color={driver.color} />
          <BulletBlock title="Points négatifs" items={driver.negatifs} color={driver.color} />
          <BulletBlock title="Risques et conséquences" items={driver.risques} color={driver.color} />
          <BulletBlock title="Point de rupture" items={driver.rupture} color={driver.color} />
          <BulletBlock title="De quoi on se coupe" items={driver.coupure} color={driver.color} />
        </div>
      </div>

      <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 sm:p-8 mb-6">
        <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-1">Racontez-vous !</h3>
        <p className="text-sm text-[var(--ink-soft)] mb-5">Notez vos situations de vie (pro/perso) pour ce driver.</p>

        <div className="space-y-6">
          {EXO_CASES.map((c) => (
            <div key={c.key} className="rounded-2xl border border-[var(--border)] bg-[var(--sand)]/50 p-4 sm:p-5">
              <label className="block text-sm font-semibold text-[var(--ink)] mb-2">{c.label}</label>
              <textarea
                rows={3}
                value={entry[c.key].text}
                onChange={(e) => setCase(c.key, e.target.value)}
                data-testid={`exo-text-${driver.key}-${c.key}`}
                placeholder="Décrivez une ou plusieurs situations…"
                className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)] resize-y"
              />
            </div>
          ))}

          <div>
            <label className="block text-sm font-semibold text-[var(--ink)] mb-2">Commentaires</label>
            <textarea
              rows={2}
              value={entry.commentaires}
              onChange={(e) => onChange({ ...entry, commentaires: e.target.value })}
              data-testid={`exo-comment-${driver.key}`}
              placeholder="Notes libres…"
              className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)] resize-y"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-8 no-print">
        <button onClick={onPrev} data-testid="exo-btn-prev" className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">Précédent</button>
        <button onClick={onNext} data-testid="exo-btn-next" className="px-8 py-3 rounded-full bg-[var(--gold)] text-white font-medium hover:brightness-105 shadow-sm">
          {index === total - 1 ? "Terminer et évaluer" : "Suivant"}
        </button>
      </div>
      <p className="text-center text-xs text-[var(--ink-soft)] mt-3 no-print" data-testid="exo-autosave-note">
        Votre progression est enregistrée automatiquement sur cet appareil.
      </p>
    </div>
  );
};

export const EvaluationStep = ({ ranking, moveRank, entries, onChangeFirstStep, onPrev, onFinish, idrPrefilled = false }) => (
  <div className="animate-fade-up" data-testid="exo-evaluation">
    <HomeLink />
    <div className="text-center mb-8">
      <h2 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-3">Évaluation finale</h2>
      <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto">
        Maintenant que vous avez parcouru les cinq drivers, prenez du recul sur l'ensemble.
      </p>
      {idrPrefilled && <p className="inline-block mt-3 text-sm text-[var(--ink-soft)] bg-[var(--sand)] border border-[var(--border)] rounded-full px-4 py-1.5" data-testid="exo-idr-prefilled">Point de départ : le classement établi lors de votre Inventaire des drivers.</p>}
    </div>

    <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 sm:p-8 mb-8" data-testid="exo-ranking">
      <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-1">Votre propre classement</h3>
      <p className="text-sm text-[var(--ink-soft)] mb-5">
        Classez les drivers du plus marquant (en haut) au moins marquant pour vous aujourd'hui. Utilisez les flèches pour réorganiser.
      </p>
      <ul className="space-y-2">
        {ranking.map((key, i) => {
          const d = DRIVER_BY_KEY[key];
          return (
            <li key={key} data-testid={`exo-rank-${key}`} className="flex items-center gap-3 rounded-2xl border border-[var(--border)] bg-[var(--sand)] p-3">
              <span className="flex items-center justify-center h-8 w-8 rounded-full text-white font-semibold shrink-0" style={{ backgroundColor: d.color }} data-testid={`exo-rank-pos-${key}`}>{i + 1}</span>
              <span className="flex-1 font-medium text-[var(--ink)]">{d.name}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => moveRank(key, -1)} disabled={i === 0} data-testid={`exo-rank-up-${key}`} className={`h-8 w-8 rounded-lg flex items-center justify-center border transition-colors ${i === 0 ? "border-[var(--border)] text-[var(--beige)] cursor-not-allowed" : "border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white"}`}>
                  <ChevronUp size={18} />
                </button>
                <button onClick={() => moveRank(key, 1)} disabled={i === ranking.length - 1} data-testid={`exo-rank-down-${key}`} className={`h-8 w-8 rounded-lg flex items-center justify-center border transition-colors ${i === ranking.length - 1 ? "border-[var(--border)] text-[var(--beige)] cursor-not-allowed" : "border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white"}`}>
                  <ChevronDown size={18} />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>

    <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 sm:p-8 mb-8" data-testid="exo-firststeps">
      <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-1">Mes premiers petits pas</h3>
      <p className="text-sm text-[var(--ink-soft)] mb-5">
        Pour les drivers qui vous correspondent fortement, choisissez un premier geste concret et réaliste à poser.
      </p>
      <div className="space-y-5">
        {EXO_DRIVERS.map((driver) => (
          <div key={driver.key} className="rounded-2xl border-2 p-4 sm:p-5" style={{ borderColor: driver.color }}>
            <label className="block text-sm font-semibold mb-1" style={{ color: driver.color }}>
              Mon premier petit pas — {driver.antidote}
            </label>
            <p className="text-xs text-[var(--ink-soft)] mb-2">
              Dans les drivers qui vous correspondent fortement et pour incarner l'antidote « {driver.antidote} », quel premier petit pas concret pourriez-vous poser dans une prochaine situation ?
            </p>
            <textarea
              rows={2}
              value={entries[driver.key].premierPas}
              onChange={(e) => onChangeFirstStep(driver.key, e.target.value)}
              data-testid={`exo-firststep-${driver.key}`}
              placeholder="Un geste simple, réaliste et concret…"
              className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)] resize-y"
            />
          </div>
        ))}
      </div>
    </div>

    <div className="flex items-center justify-between mt-8 no-print">
      <button onClick={onPrev} data-testid="exo-btn-evaluation-prev" className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">Précédent</button>
      <button onClick={onFinish} data-testid="exo-btn-finish" className="px-8 py-3 rounded-full bg-[var(--gold)] text-white font-medium hover:brightness-105 shadow-sm">Voir la synthèse</button>
    </div>
  </div>
);
